import constants from '@/constants'
import { useActiveLanguage } from '@/hooks'
import useIsMobileSize from '@/hooks/useIsMobileSize'
import { i18n } from '@/locale/i18n'
import { formatNumber } from '@/util/localizeNumber'
import React from 'react'
import { useState, useEffect, useRef, useMemo, useCallback } from 'react'
import '@/features/market/place-bet/PlaceBet.scss'
import './OrderBook.scss'
import { Crevron, InfoIcon } from '@/assets/icons'
// import { useAutoAnimate } from '@formkit/auto-animate/react'
import OverlayPanel from '@/components/OverlayPanel/OverlayPanel'
import { useDispatch, useSelector } from 'react-redux'
import {
  setSelectedLimitPrice,
  selectSelectedLimitPrice,
  selectPageLayout,
  selectLimitOrderFillPreview,
} from '@/store/app'
import Currency from '@/components/currency/Currency'
import TradingHaltNotice from '@/features/trading-halt'
import { useCurrencies } from '@/hooks'
import CurrencyIcon from '@/components/currencyIcon'
import ButtonIcon from '@/components/ui/ButtonIcon/ButtonIcon'
import OrderBookChart, { orderBookScrollOffsets } from './OrderBookChart'
import OutcomeSelector from './OutcomeSelector'
import useCurrentUser from '@/hooks/useCurrentUser'
import { getPositionLabels } from '@/features/market/utils/positionLabels'
import { deriveMarketUi } from '@/features/market/utils/marketType'

const OrderBook = React.memo(
  ({
    question,
    onChangeOutcome = () => {},
    currency,
    orders,
    isLoading,
    spread,
    outcome,
    positionType,
    setOutcome,
    setPositionType,
    onInfoClick = () => {},
    wagerSelected = null,
    userOrders = [],
    open = false,
    setOpen = () => {},
    isMarketPage = false, // New prop to determine if we should preload content
    onPriceClick = () => {}, // Callback for when a price is clicked (for mobile PlaceBet opening)
    showHeader = true, // Prop to control header visibility
    selectorType = 'dropdown', // 'dropdown' or 'tabs'
    showTabs = false,
    onSetActiveTab = () => {}, // Callback to switch to buy/sell tab
  }) => {
    const locale = useActiveLanguage()
    const isMobileSize = useIsMobileSize()
    const containerRef = useRef(null)
    const scrollOffsetRef = useRef(null)
    const hadOrderDataRef = useRef(false)
    const user = useCurrentUser()
    // const [animationParent] = useAutoAnimate()
    const animationParent = useRef(null)
    const dispatch = useDispatch()
    const selectedLimitPrice = useSelector(selectSelectedLimitPrice)
    const limitOrderFillPreview = useSelector(selectLimitOrderFillPreview)
    const { adminMode } = useSelector(selectPageLayout)
    const { currencies } = useCurrencies()

    // Show header and content area from the start so the bar (title, info icon, dropdown) is visible before data arrives
    const [contentLoaded] = useState(true)

    // State to stabilize loading transitions
    const [isStableLoading, setIsStableLoading] = useState(false)

    // Stabilize loading state to prevent excessive re-renders during transitions
    useEffect(() => {
      const timer = setTimeout(() => {
        setIsStableLoading(isLoading)
      }, 50) // Small delay to debounce loading state changes

      return () => clearTimeout(timer)
    }, [isLoading])

    // Compute effective outcome - use first outcome as default on mobile if none provided
    const effectiveOutcome = useMemo(() => {
      if (outcome?.id) return outcome

      // On mobile, if no outcome is selected, use the first outcome for OrderBook display
      // This doesn't affect parent state, just internal OrderBook behavior
      if (isMobileSize && question?.outcomes?.length > 0) {
        return question.outcomes[0]
      }

      return outcome
    }, [outcome?.id, isMobileSize, question?.outcomes?.length])

    // Get position labels from outcome (long_label/short_label) instead of hardcoded Yes/No
    const positionLabels = useMemo(() => {
      return getPositionLabels(effectiveOutcome || question?.outcomes?.[0], question)
    }, [effectiveOutcome, question])
    const i18nYes = positionLabels.longLabel
    const i18nNo = positionLabels.shortLabel

    const scrollCacheKey = `${effectiveOutcome?.id ?? 'none'}-${positionType}`

    const persistScrollFromContainer = useCallback(() => {
      const scrollTop = containerRef.current?.scrollTop
      if (typeof scrollTop === 'number' && scrollTop >= 0) {
        scrollOffsetRef.current = scrollTop
        orderBookScrollOffsets.set(scrollCacheKey, scrollTop)
      }
    }, [scrollCacheKey])

    useEffect(() => {
      scrollOffsetRef.current = null
      hadOrderDataRef.current = false
    }, [scrollCacheKey])

    // Note: Scrolling is now handled by OrderBookChart's internal logic

    // Find matching user order for a given price
    // const findMatchingUserOrder = price => {
    //   if (!userOrders || userOrders.length === 0) return null
    //   return userOrders.find(userOrder => userOrder.price === price && userOrder.outcome_id === outcome?.id)
    // }

    // Calculate order progress percentage
    // const calculateOrderProgress = userOrder => {
    //   if (!userOrder || userOrder.shares_requested === 0) return 0
    //   return Math.round((userOrder.shares / userOrder.shares_requested) * 100)
    // }

    // Create formatting functions to pass to UI components - memoized to prevent recreation
    const formatNumberForUI = useCallback(
      (value, min = 2, max = 2) => {
        return formatNumber(value, locale, min, max)
      },
      [locale]
    )

    // Virtual outcomes (mono custom) carry the physical outcome under `_actualOutcome` and the
    // side under `_virtualPosition`; resolve back to the outcome the question actually holds so
    // consumers can match it against `question.outcomes`.
    const resolveQuestionOutcome = useCallback(
      candidate => {
        if (!candidate) return candidate
        const physical = candidate._actualOutcome || candidate
        return question?.outcomes?.find(o => String(o.id) === String(physical.id)) || physical
      },
      [question]
    )

    const handlePriceSelect = useCallback(
      price => {
        persistScrollFromContainer()
        dispatch(setSelectedLimitPrice(price))

        // On mobile, open PlaceBet when a price is clicked and pass the outcome
        if (isMobileSize && onPriceClick) {
          onPriceClick(price, resolveQuestionOutcome(effectiveOutcome), effectiveOutcome?._virtualPosition || positionType)
        }
      },
      [
        dispatch,
        isMobileSize,
        onPriceClick,
        effectiveOutcome,
        positionType,
        resolveQuestionOutcome,
        persistScrollFromContainer,
      ]
    )

    const handleInfoClick = useCallback(
      (total_user_shares_requested, total_user_shares) => {
        // This could be enhanced to show more detailed info
        onInfoClick()
      },
      [onInfoClick]
    )

    // Handlers for outcome selector - memoized to prevent recreation
    const handleOutcomeChange = useCallback(
      selectedOutcome => {
        // Switch to buy tab when outcome changes
        onSetActiveTab('buy')
        setOutcome(selectedOutcome)
        onChangeOutcome(selectedOutcome)

        // On mobile, open PlaceBet when an outcome is selected (e.g., clicking Yes/No buttons)
        if (isMobileSize && onPriceClick && selectedOutcome) {
          onPriceClick(
            null,
            resolveQuestionOutcome(selectedOutcome),
            selectedOutcome?._virtualPosition || positionType
          )
        }
      },
      [
        setOutcome,
        onChangeOutcome,
        onSetActiveTab,
        isMobileSize,
        onPriceClick,
        positionType,
        resolveQuestionOutcome,
      ]
    )

    const handlePositionChange = useCallback(
      position => {
        setPositionType(position)
      },
      [setPositionType]
    )

    const handleToggleOpen = useCallback(() => {
      setOpen(v => !v)
    }, [setOpen])

    // Check if orderbook is crossed (prices not sorted correctly)
    // Only visible in staff mode
    const fillPreviewByPrice = useMemo(() => {
      if (!adminMode) return null
      if (!limitOrderFillPreview?.previewByPrice) return null
      if (limitOrderFillPreview.outcomeId !== effectiveOutcome?.id) return null
      if (limitOrderFillPreview.position !== positionType) return null
      return limitOrderFillPreview.previewByPrice
    }, [adminMode, limitOrderFillPreview, effectiveOutcome?.id, positionType])

    const isOrderBookCrossed = useMemo(() => {
      if (!adminMode || !user?.is_staff) return false
      if (!orders?.buySeries || !orders?.sellSeries) return false

      const buySeries = orders.buySeries
      const sellSeries = orders.sellSeries
      // Check buy series: should be in descending order (highest to lowest)
      for (let i = 1; i < buySeries.length; i++) {
        if (buySeries[i].price > buySeries[i - 1].price) {
          console.log('buySeries is not sorted correctly', buySeries[i].price, buySeries[i - 1].price)
          return true // Buy series is not sorted correctly
        }
      }

      // Check sell series: should be in ascending order (lowest to highest)
      for (let i = 1; i < sellSeries.length; i++) {
        if (sellSeries[i].price > sellSeries[i - 1].price) {
          console.log('sellSeries is not sorted correctly', sellSeries[i].price, sellSeries[i - 1].price)
          return true // Sell series is not sorted correctly
        }
      }

      return false
    }, [orders?.buySeries, orders?.sellSeries, adminMode, user?.is_staff])

    // Memoize outcome selector to prevent unnecessary re-renders
    const outcomeSelectorElement = useMemo(() => {
      if (selectorType === 'tabs' && showTabs) {
        return (
          <div>
            <OutcomeSelector
              question={question}
              outcome={effectiveOutcome}
              positionType={positionType}
              wagerSelected={wagerSelected}
              onOutcomeChange={handleOutcomeChange}
              onPositionChange={handlePositionChange}
              constants={constants}
              i18nYes={i18nYes}
              i18nNo={i18nNo}
              i18nSelectBet={i18n('place_bet.select_bet')}
              formatNumber={formatNumberForUI}
              currencies={currencies}
              CurrencyIcon={CurrencyIcon}
              variant="tabs"
            />
          </div>
        )
      }

      if (showHeader) {
        return (
          <div className="!mb-4">
            <div className="flex justify-between items-center px-3 py-2 h-16 border border-Neutral-3 border-solid gap-3 md:gap-6 rounded-lg !mt-2 relative">
              <OutcomeSelector
                question={question}
                outcome={effectiveOutcome}
                positionType={positionType}
                wagerSelected={wagerSelected}
                onOutcomeChange={handleOutcomeChange}
                onPositionChange={handlePositionChange}
                constants={constants}
                i18nYes={i18nYes}
                i18nNo={i18nNo}
                i18nSelectBet={i18n('place_bet.select_bet')}
                formatNumber={formatNumberForUI}
                currencies={currencies}
                CurrencyIcon={CurrencyIcon}
              />
            </div>
          </div>
        )
 
      }
    }, [
      selectorType,
      question?.id,
      effectiveOutcome?.id,
      positionType,
      wagerSelected?.id,
      handleOutcomeChange,
      handlePositionChange,
      formatNumberForUI,
      currencies,
      i18nYes,
      i18nNo,
    ])

    // Determine if we have valid order data (check if data exists, regardless of loading state)
    // Check if orders object exists and has the expected structure with actual data
    const hasOrderData =
      orders &&
      orders.buySeries &&
      orders.sellSeries &&
      Array.isArray(orders.buySeries) &&
      Array.isArray(orders.sellSeries) &&
      (orders.buySeries.length > 0 || orders.sellSeries.length > 0)

    // Show skeleton only when loading AND we don't have data yet
    const shouldShowSkeleton = isStableLoading && !hasOrderData
    if (hasOrderData) {
      hadOrderDataRef.current = true
    }
    const showChartSkeleton = shouldShowSkeleton && !hadOrderDataRef.current

    const orderBookUi = useMemo(() => deriveMarketUi(question), [
      question?.markets_correlation,
      question?.outcomes?.length,
      question?.outcomes?.[0]?.position_labels,
      question?.outcomes?.[1]?.position_labels,
    ])
    const hideOrderBookLabels = Boolean(orderBookUi.binaryLabelStyle) || orderBookUi.isMono

    return (
      <div className="flex flex-col order-book" ref={animationParent}>
        <TradingHaltNotice />
        {/* Show header based on showHeader prop */}
        {showHeader && (
          <div
            className={`flex justify-between items-center gap-4 cursor-pointer rounded-lg ${
              isOrderBookCrossed ? 'bg-red-100 dark:bg-red-900/30 border border-red-300 dark:border-red-700' : ''
            }`}
            onClick={handleToggleOpen}>
            <div className="flex gap-4 items-center">
              <span
                className={`market-h2 whitespace-pre ${isOrderBookCrossed ? 'text-red-700 dark:text-red-400' : ''}`}>
                {i18n('order_book.title')}
              </span>
              {!isOrderBookCrossed && (
                <OverlayPanel
                  content={i18n('order_book.description')}
                  position="top-middle-up-middle"
                  containerClassName="!flex items-center justify-center"
                  showDelay={50}
                  hideDelay={50}
                  showMode="hover"
                  isOverlayEnabled={true}>
                  <InfoIcon width="16px" />
                </OverlayPanel>
              )}
              {/* Crossed orderbook warning - only visible in staff mode */}
              {isOrderBookCrossed && (
                <span className="text-sm font-semibold text-red-700 dark:text-red-400">
                  ⚠️ Crossed Orderbook - This may indicate a data integrity issue.
                </span>
              )}
            </div>
            <ButtonIcon
              size="small"
              cursorPointer
              className={isOrderBookCrossed ? 'order-book__toggle--crossed' : ''}
              handleOnClick={event => {
                // The whole header toggles; without this the row handler would undo it.
                event.stopPropagation()
                handleToggleOpen()
              }}
              ariaLabel={i18n(open ? 'order_book.hide' : 'order_book.show')}>
              <Crevron direction={open ? 'up' : 'down'} />
            </ButtonIcon>
          </div>
        )}

        {/* Content - Always rendered when loaded, but hidden/shown with CSS */}
        {(contentLoaded || !showHeader) && (
          <div
            className="flex flex-col overflow-hidden transition-all duration-300 ease-in-out"
            style={{
              maxHeight: open || !showHeader ? '500px' : '0',
              opacity: open || !showHeader ? 1 : 0,
              visibility: open || !showHeader ? 'visible' : 'hidden',
            }}>
            <div className="flex flex-col">
              {outcomeSelectorElement}
              {/* Show skeleton only when loading AND no data, otherwise show OrderBookChart */}
              {showChartSkeleton ? (
                <div id="chart-order-book" className="h-72">
                  <div className="flex flex-col gap-1">
                    {Array.from({ length: 4 }).map((_, index) => (
                      <div key={`skeleton-buy-${index}`} className="flex items-center gap-4 h-6">
                        <div className="flex-1 relative h-full">
                          <div
                            className="h-full opacity-30 rounded skeleton"
                            style={{ width: `${Math.min(20 + (index * 60) / 3, 80)}%` }}
                          />
                        </div>
                        <div className="w-16">
                          <div className="h-3 rounded w-12 skeleton" />
                        </div>
                        <div className="w-16">
                          <div className="h-3 rounded w-10 skeleton" />
                        </div>
                        <div className="w-16">
                          <div className="h-3 rounded w-12 skeleton" />
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="border-t border-b border-solid border-Neutral-3 py-2 my-1" />
                  <div className="flex flex-col gap-1">
                    {Array.from({ length: 4 }).map((_, index) => (
                      <div key={`skeleton-sell-${index}`} className="flex items-center gap-4 h-6">
                        <div className="flex-1 relative h-full">
                          <div
                            className="h-full opacity-30 rounded skeleton"
                            style={{ width: `${80 - (index * 60) / 3}%` }}
                          />
                        </div>
                        <div className="w-16">
                          <div className="h-3 rounded w-12 skeleton" />
                        </div>
                        <div className="w-16">
                          <div className="h-3 rounded w-10 skeleton" />
                        </div>
                        <div className="w-16">
                          <div className="h-3 rounded w-12 skeleton" />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <OrderBookChart
                  orders={orders}
                  isLoading={isStableLoading}
                  spread={spread}
                  lastPrice={orders?.lastPrice}
                  selectedPrice={selectedLimitPrice}
                  onPriceSelect={handlePriceSelect}
                  formatNumber={formatNumberForUI}
                  currency={currency}
                  onInfoClick={onInfoClick}
                  me={user || null}
                  InfoIcon={InfoIcon}
                  containerRef={containerRef}
                  scrollOffsetRef={scrollOffsetRef}
                  scrollCacheKey={scrollCacheKey}
                  CurrencyComponent={Currency}
                  isVisible={open && contentLoaded}
                  positionType={positionType}
                  constants={constants}
                  i18nYes={i18nYes}
                  i18nNo={i18nNo}
                  adminMode={adminMode}
                  fillPreviewByPrice={fillPreviewByPrice}
                  hideOrderBookColHeaders={{
                    labels: hideOrderBookLabels,
                    cumulativeShares: !adminMode,
                  }}
                  yesnoMarket={orderBookUi.binaryLabelStyle === 'yesno'}
                />
              )}
            </div>
          </div>
        )}
      </div>
    )
  }
)

OrderBook.displayName = 'OrderBook'

export default OrderBook
